"use client";

import Link from "next/link";
import { useState } from "react";
import { motion } from "framer-motion";
import { CheckCircle2 } from "lucide-react";
import { fadeUp, cardSlideUp, staggerContainer } from "@/lib/motion";
import { useSnapCarousel } from "@/lib/useSnapCarousel";
import CarouselDots from "./CarouselDots";

const plans = [
  {
    name: "Starter",
    tag: "Starter",
    price: 49,
    description: "For individuals & small teams",
    features: [
      "5 datasets",
      "100K API calls/mo",
      "Standard support",
      "1 API key",
    ],
    cta: "Get Started",
    highlight: false,
    boxClass: "bg-black/5",
  },
  {
    name: "Pro",
    tag: "Pro",
    price: 149,
    description: "For growing agencies",
    features: [
      "50 datasets",
      "2M API calls/mo",
      "Priority support",
      "5 API keys",
      "Analytics dashboard",
    ],
    cta: "Start free Trial",
    highlight: true,
    boxClass: "bg-gradient-to-br from-cream-dark to-cream/40",
  },
  {
    name: "Custom",
    tag: "Enterprise",
    price: null,
    description: "For large organizations",
    features: [
      "Unlimited datasets",
      "Unlimited API calls",
      "Dedicated support",
      "Unlimited keys",
      "Custom SLA",
      "SSO",
    ],
    cta: "Contact Sales",
    highlight: false,
    boxClass: "bg-gradient-to-br from-ink/5 to-ink/0",
  },
];

function PlanCard({
  plan,
  yearly,
}: {
  plan: (typeof plans)[number];
  yearly: boolean;
}) {
  return (
    <div
      className={`relative flex h-full flex-col overflow-hidden rounded-2xl border ${
        plan.highlight
          ? "border-brand bg-white shadow-xl shadow-brand/10"
          : "border-black/5 bg-white shadow-sm"
      }`}
    >
      <div className={`relative px-5 pb-4 pt-5 ${plan.boxClass}`}>
        <span
          className={`inline-block rounded-full px-3 py-1 text-xs font-semibold ${
            plan.highlight ? "bg-brand text-white" : "bg-ink text-white"
          }`}
        >
          {plan.tag}
        </span>

        <div className="mt-2.5 flex items-baseline gap-1">
          {plan.price !== null ? (
            <>
              <span className="text-3xl font-bold text-ink sm:text-4xl">
                ${yearly ? Math.round(plan.price * 0.8) : plan.price}
              </span>
              <span className="text-sm text-ink-soft">/mo</span>
            </>
          ) : (
            <span className="text-3xl font-bold text-ink sm:text-4xl">
              Custom
            </span>
          )}
        </div>
        <p className="mt-1 text-sm text-ink-soft">{plan.description}</p>
      </div>

      <div className="flex flex-1 flex-col px-5 pb-5">
        <ul className="mt-4 flex-1 space-y-2.5">
          {plan.features.map((feature) => (
            <li
              key={feature}
              className="flex items-center gap-2 text-sm text-ink-soft"
            >
              <CheckCircle2 className="h-4 w-4 shrink-0 text-brand" />
              {feature}
            </li>
          ))}
        </ul>

        <Link href="/signup" className="mt-5 block w-full">
          <motion.button
            whileHover={{ scale: 1.02 }}
            whileTap={{ scale: 0.98 }}
            className={`w-full rounded-full px-5 py-3 text-sm font-medium transition-colors ${
              plan.highlight
                ? "bg-gradient-to-br from-brand to-brand-dark text-white shadow-lg shadow-brand/30 hover:brightness-105"
                : "border border-black/10 bg-white text-ink hover:bg-black/5"
            }`}
          >
            {plan.cta}
          </motion.button>
        </Link>
      </div>
    </div>
  );
}

export default function Pricing() {
  const [yearly, setYearly] = useState(true);
  const { containerRef, setItemRef, active, scrollTo } = useSnapCarousel(
    plans.length,
    1
  );

  return (
    <section className="py-20 sm:py-28">
      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex flex-col items-start justify-between gap-6 sm:flex-row sm:items-end">
          <motion.h2
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            className="max-w-md text-3xl font-semibold tracking-tight text-ink sm:text-4xl"
          >
            Simple, transparent pricing
          </motion.h2>
          <motion.div
            variants={fadeUp}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-50px" }}
            custom={1}
            className="flex flex-col items-start gap-4 sm:items-end"
          >
            <p className="text-sm text-ink-soft">
              Start free. Scale as you grow. No hidden fees.
            </p>

            {/* Mobile badge (replaces toggle on small screens) */}
            <span className="rounded-full border border-black/10 bg-white px-4 py-1.5 text-xs font-medium text-ink-soft sm:hidden">
              Pricing
            </span>

            {/* Desktop toggle */}
            <div className="hidden items-center gap-1 rounded-full border border-black/10 bg-white p-1 sm:flex">
              <button
                onClick={() => setYearly(true)}
                className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                  yearly
                    ? "bg-brand text-white"
                    : "text-ink-soft hover:text-ink"
                }`}
              >
                Yearly
              </button>
              <button
                onClick={() => setYearly(false)}
                className={`rounded-full px-4 py-1.5 text-xs font-medium transition-colors ${
                  !yearly
                    ? "bg-brand text-white"
                    : "text-ink-soft hover:text-ink"
                }`}
              >
                Monthly
              </button>
            </div>
          </motion.div>
        </div>

        {/* Divider Line */}
        <div className="my-10 h-px w-full bg-black/5 sm:my-14" />

        {/* Mobile carousel */}
        <div className="sm:hidden">
          <div
            ref={containerRef}
            className="flex snap-x snap-mandatory gap-3 overflow-x-auto px-[4%] pb-2 scrollbar-none"
          >
            {plans.map((plan, i) => (
              <div
                key={plan.name}
                ref={setItemRef(i)}
                className="w-[92%] shrink-0 snap-center"
              >
                <PlanCard plan={plan} yearly={yearly} />
              </div>
            ))}
          </div>
          <CarouselDots count={plans.length} active={active} onSelect={scrollTo} />
        </div>

        {/* Desktop grid */}
        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-50px" }}
          className="mt-14 hidden grid-cols-1 gap-6 sm:grid lg:grid-cols-3"
        >
          {plans.map((plan, i) => (
            <motion.div
              key={plan.name}
              variants={cardSlideUp}
              custom={i}
              whileHover={{ y: -6, transition: { duration: 0.25, ease: "easeOut" } }}
            >
              <PlanCard plan={plan} yearly={yearly} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
